import React, { useState, useEffect, useRef, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { head } from "lodash";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import Chip from '@material-ui/core/Chip';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { isNil } from "lodash";
import { i18n } from "../../translate/i18n";
import moment from "moment";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
  FormControl,
  MenuItem,
  Select,
  Tab,
  Tabs,
  FormControlLabel,
  Switch,
  Collapse,
  List,
  ListItem,
  ListItemText,
  FormHelperText,
  Checkbox,
  FormGroup,
  Tooltip,
} from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import ConfirmationModal from "../ConfirmationModal";
import UserStatusIcon from "../UserModal/statusIcon";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import useQueues from "../../hooks/useQueues";
import { getTokens } from "../../styles/tokens";
import { filterOutboundConnections } from "../../utils/channels";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    root: {
      display: "flex",
      flexWrap: "wrap",
    },
    // o <Form> fica entre o papel do diálogo e o conteúdo: precisa ser coluna flex
    // para o conteúdo rolar e o rodapé (Salvar) continuar visível
    form: {
      display: "flex",
      flexDirection: "column",
      flex: "1 1 auto",
      minHeight: 0,
    },
    formGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: 16,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    full: { gridColumn: "1 / -1" },
    field: { display: "flex", flexDirection: "column", gap: 6, minWidth: 0 },
    label: { fontSize: 12.5, fontWeight: 600, color: t.textSecondary },
    help: { fontSize: 12, color: t.textMuted },
    placeholder: { color: t.textMuted },
    sectionTitle: {
      gridColumn: "1 / -1",
      fontSize: 13,
      fontWeight: 600,
      color: t.text,
      margin: "8px 0 0",
      paddingTop: 12,
      borderTop: `1px solid ${t.line}`,
    },
    sectionTitleFirst: { margin: 0, paddingTop: 0, borderTop: "none" },
    recurrenceFields: { marginTop: 12 },
    previewBox: {
      marginTop: 12,
      maxHeight: 220,
      overflow: "auto",
      padding: "12px 16px 4px",
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.sm,
      backgroundColor: t.muted,
      ...theme.scrollbarStyles,
    },
    previewTitle: { fontSize: 12.5, fontWeight: 600, color: t.textSecondary },
    tabs: {
      borderBottom: `1px solid ${t.line}`,
      marginBottom: 16,
    },
    messageGrid: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)",
      gap: 16,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    attachment: {
      gridColumn: "1 / -1",
      display: "flex",
      alignItems: "center",
      gap: 8,
      minWidth: 0,
      padding: "6px 8px 6px 12px",
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.sm,
      backgroundColor: t.muted,
      color: t.text,
      fontSize: 13,
      "& > .MuiSvgIcon-root": { fontSize: 18, color: t.textMuted, flex: "none" },
    },
    attachmentName: {
      flex: 1,
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    danger: { color: t.semantic.error },
    actions: { flexWrap: "wrap" },
    actionsLeft: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: 8,
      marginRight: "auto",
    },
    btnWrapper: {
      position: "relative",
    },
    buttonProgress: {
      color: t.brand,
      position: "absolute",
      top: "50%",
      left: "50%",
      marginTop: -12,
      marginLeft: -12,
    },
  };
});

const RECURRENCE_TYPES = [
  ["minutely", "Por Minuto"],
  ["hourly", "Por Hora"],
  ["daily", "Diário"],
  ["weekly", "Semanal"],
  ["biweekly", "Quinzenal"],
  ["monthly", "Mensal"],
  ["yearly", "Anual"],
];

// No CampaignModal.js - Atualizar o schema de validação

const CampaignSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  isRecurring: Yup.boolean().default(false),
  recurrenceType: Yup.string().when('isRecurring', {
    is: true,
    then: Yup.string().oneOf(['minutely', 'hourly', 'daily', 'weekly', 'biweekly', 'monthly', 'yearly']).required('Tipo de recorrência é obrigatório'),
    otherwise: Yup.string().nullable()
  }),
  recurrenceInterval: Yup.number().when('isRecurring', {
    is: true,
    then: Yup.number().min(1, 'Intervalo deve ser maior que 0').required('Intervalo é obrigatório'),
    otherwise: Yup.number().nullable()
  }),
  // CORREÇÃO: Validar como array mas permitir vazio
  recurrenceDaysOfWeek: Yup.array().when(['isRecurring', 'recurrenceType'], {
    is: (isRecurring, recurrenceType) => isRecurring && recurrenceType === 'weekly',
    then: Yup.array().min(1, 'Selecione pelo menos um dia da semana').required(),
    otherwise: Yup.array().nullable()
  }),
  recurrenceDayOfMonth: Yup.number().when(['isRecurring', 'recurrenceType'], {
    is: (isRecurring, recurrenceType) => isRecurring && recurrenceType === 'monthly',
    then: Yup.number().min(1, 'Dia deve ser entre 1 e 31').max(31, 'Dia deve ser entre 1 e 31').required('Dia do mês é obrigatório'),
    otherwise: Yup.number().nullable()
  }),
  recurrenceEndDate: Yup.date().when('isRecurring', {
    is: true,
    then: Yup.date().min(new Date(), 'Data final deve ser futura').nullable(),
    otherwise: Yup.date().nullable()
  }),
  maxExecutions: Yup.number().when('isRecurring', {
    is: true,
    then: Yup.number().min(1, 'Número máximo deve ser maior que 0').nullable(),
    otherwise: Yup.number().nullable()
  })
});

const CampaignModal = ({
  open,
  onClose,
  campaignId,
  initialValues,
  onSave,
  resetPagination,
}) => {
  const classes = useStyles();
  const isMounted = useRef(true);
  const { user, socket } = useContext(AuthContext);
  const { companyId } = user;

  const initialState = {
    name: "",
    message1: "",
    message2: "",
    message3: "",
    message4: "",
    message5: "",
    confirmationMessage1: "",
    confirmationMessage2: "",
    confirmationMessage3: "",
    confirmationMessage4: "",
    confirmationMessage5: "",
    status: "INATIVA",
    confirmation: false,
    scheduledAt: "",
    contactListId: "",
    tagListId: "Nenhuma",
    companyId,
    statusTicket: "closed",
    openTicket: "disabled",
    // Novos campos de recorrência
    isRecurring: false,
    recurrenceType: "",
    recurrenceInterval: 1,
    recurrenceDaysOfWeek: [],
    recurrenceDayOfMonth: 1,
    recurrenceEndDate: "",
    maxExecutions: null,
  };

  const [campaign, setCampaign] = useState(initialState);
  const [whatsapps, setWhatsapps] = useState([]);
  const [selectedWhatsapps, setSelectedWhatsapps] = useState([]);
 const [whatsappId, setWhatsappId] = useState(null);
  const [contactLists, setContactLists] = useState([]);
  const [tagLists, setTagLists] = useState([]);
  const [messageTab, setMessageTab] = useState(0);
  const [attachment, setAttachment] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [campaignEditable, setCampaignEditable] = useState(true);
  const [previewExecutions, setPreviewExecutions] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const attachmentFile = useRef(null);

  const [options, setOptions] = useState([]);
  const [queues, setQueues] = useState([]);
  const [allQueues, setAllQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const { findAll: findAllQueues } = useQueues();

  // Opções para dias da semana
  const daysOfWeekOptions = [
    { value: 0, label: 'Domingo' },
    { value: 1, label: 'Segunda-feira' },
    { value: 2, label: 'Terça-feira' },
    { value: 3, label: 'Quarta-feira' },
    { value: 4, label: 'Quinta-feira' },
    { value: 5, label: 'Sexta-feira' },
    { value: 6, label: 'Sábado' },
  ];

  // Função para preview das execuções
  const handlePreviewRecurrence = async (values) => {
    if (!values.isRecurring || !values.recurrenceType || !values.scheduledAt) {
      setPreviewExecutions([]);
      return;
    }

    try {
      const params = {
        recurrenceType: values.recurrenceType,
        recurrenceInterval: values.recurrenceInterval,
        recurrenceDaysOfWeek: JSON.stringify(values.recurrenceDaysOfWeek),
        recurrenceDayOfMonth: values.recurrenceDayOfMonth,
      };

      if (campaignId) {
        const { data } = await api.get(`/campaigns/${campaignId}/recurrence-preview`, { params });
        setPreviewExecutions(data.executions);
      } else {
        // Para campanhas novas, calcular localmente ou usar endpoint genérico
        const mockExecutions = calculateMockExecutions(values);
        setPreviewExecutions(mockExecutions);
      }
    } catch (err) {
      console.error('Erro ao buscar preview:', err);
    }
  };

  // Função auxiliar para calcular execuções mock
  const calculateMockExecutions = (values) => {
    const executions = [];
    let currentDate = moment(values.scheduledAt);
    
    for (let i = 0; i < 5; i++) {
      executions.push(currentDate.format('DD/MM/YYYY HH:mm'));
      
      switch (values.recurrenceType) {
        case 'minutely':
          currentDate = currentDate.clone().add(values.recurrenceInterval, 'minutes');
          break;
        case 'hourly':
          currentDate = currentDate.clone().add(values.recurrenceInterval, 'hours');
          break;
        case 'daily':
          currentDate = currentDate.clone().add(values.recurrenceInterval, 'days');
          break;
        case 'weekly':
          currentDate = currentDate.clone().add(values.recurrenceInterval, 'weeks');
          break;
        case 'biweekly':
          currentDate = currentDate.clone().add(values.recurrenceInterval * 2, 'weeks');
          break;
        case 'monthly':
          currentDate = currentDate.clone().add(values.recurrenceInterval, 'months');
          if (values.recurrenceDayOfMonth) {
            currentDate = currentDate.date(values.recurrenceDayOfMonth);
          }
          break;
        case 'yearly':
          currentDate = currentDate.clone().add(values.recurrenceInterval, 'years');
          break;
      }
    }
    
    return executions;
  };

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      const loadQueues = async () => {
        const list = await findAllQueues();
        setAllQueues(list);
        setQueues(list);
      };
      loadQueues();
    }
  }, []);

  useEffect(() => {
    if (searchParam.length < 3) {
      setLoading(false);
      setSelectedQueue("");
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      setLoading(true);
      const fetchUsers = async () => {
        try {
          const { data } = await api.get("/users/");
          setOptions(data.users);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchUsers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam]);

  useEffect(() => {
    if (isMounted.current) {
      if (initialValues) {
        setCampaign((prevState) => {
          return { ...prevState, ...initialValues };
        });
      }

      api
        .get(`/contact-lists/list`, { params: { companyId } })
        .then(({ data }) => setContactLists(data));

      api
        .get(`/whatsapp`, { params: { companyId, session: 0 } })
        .then(({ data }) => {
          const mappedWhatsapps = data.map((whatsapp) => ({
            ...whatsapp,
            selected: false,
          }));
          setWhatsapps(filterOutboundConnections(mappedWhatsapps));
        });

      api.get(`/tags/list`, { params: { companyId, kanban: 0 } })
        .then(({ data }) => {
          const fetchedTags = data;
          const formattedTagLists = fetchedTags
            .filter(tag => tag.contacts.length > 0)
            .map((tag) => ({
              id: tag.id,
              name: `${tag.name} (${tag.contacts.length})`,
            }));
          setTagLists(formattedTagLists);
        })
        .catch((error) => {
          console.error("Error retrieving tags:", error);
        });

      if (!campaignId) return;

      api.get(`/campaigns/${campaignId}`).then(({ data }) => {
        if (data?.user) setSelectedUser(data.user);
        if (data?.queue) setSelectedQueue(data.queue.id);
        if (data?.whatsappId) {
          setWhatsappId(parseInt(data.whatsappId)); // Converter para número
        } else {
          setWhatsappId(null);
        }
        
        setCampaign((prev) => {
          let prevCampaignData = Object.assign({}, prev);

          Object.entries(data).forEach(([key, value]) => {
            if (key === "scheduledAt" && value !== "" && value !== null) {
              prevCampaignData[key] = moment(value).format("YYYY-MM-DDTHH:mm");
            } else if (key === "recurrenceEndDate" && value !== "" && value !== null) {
              prevCampaignData[key] = moment(value).format("YYYY-MM-DD");
            } else if (key === "recurrenceDaysOfWeek" && value) {
              prevCampaignData[key] = JSON.parse(value);
            } else {
              prevCampaignData[key] = value === null ? "" : value;
            }
          });

          return prevCampaignData;
        });
      });
    }
  }, [campaignId, open, initialValues, companyId]);

  useEffect(() => {
    const now = moment();
    const scheduledAt = moment(campaign.scheduledAt);
    const moreThenAnHour =
      !Number.isNaN(scheduledAt.diff(now)) && scheduledAt.diff(now, "hour") > 1;
    const isEditable =
      campaign.status === "INATIVA" ||
      (campaign.status === "PROGRAMADA" && moreThenAnHour);

    setCampaignEditable(isEditable);
  }, [campaign.status, campaign.scheduledAt]);

  const handleClose = () => {
    onClose();
    setCampaign(initialState);
    setPreviewExecutions([]);
    setShowPreview(false);
  };

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) {
      setAttachment(file);
    }
  };

const handleSaveCampaign = async (values) => {
  try {
    const dataValues = {
      ...values,
      whatsappId: whatsappId,
      userId: selectedUser?.id || null,
      queueId: selectedQueue || null,
      // CORREÇÃO: Garantir conversão correta do array
      recurrenceDaysOfWeek: (values.isRecurring && values.recurrenceDaysOfWeek && values.recurrenceDaysOfWeek.length > 0) 
        ? values.recurrenceDaysOfWeek // Enviar array, o backend irá converter para JSON
        : null, // Enviar null se não for recorrente ou array vazio
    };

    // Processar datas
    Object.entries(values).forEach(([key, value]) => {
      if (key === "scheduledAt" && value !== "" && value !== null) {
        dataValues[key] = moment(value).format("YYYY-MM-DD HH:mm:ss");
      } else if (key === "recurrenceEndDate" && value !== "" && value !== null) {
        dataValues[key] = moment(value).format("YYYY-MM-DD HH:mm:ss");
      } else if (key !== "recurrenceDaysOfWeek") { // Não processar recurrenceDaysOfWeek aqui
        dataValues[key] = value === "" ? null : value;
      }
    });

    // Garantir que campos não recorrentes sejam null quando isRecurring é false
    if (!values.isRecurring) {
      dataValues.recurrenceType = null;
      dataValues.recurrenceInterval = null;
      dataValues.recurrenceDaysOfWeek = null;
      dataValues.recurrenceDayOfMonth = null;
      dataValues.recurrenceEndDate = null;
      dataValues.maxExecutions = null;
    }

    if (campaignId) {
      await api.put(`/campaigns/${campaignId}`, dataValues);
      if (attachment != null) {
        const formData = new FormData();
        formData.append("file", attachment);
        await api.post(`/campaigns/${campaignId}/media-upload`, formData);
      }
      handleClose();
    } else {
      const { data } = await api.post("/campaigns", dataValues);
      if (attachment != null) {
        const formData = new FormData();
        formData.append("file", attachment);
        await api.post(`/campaigns/${data.id}/media-upload`, formData);
      }
      if (onSave) {
        onSave(data);
      }
      handleClose();
    }
    toast.success(i18n.t("campaigns.toasts.success"));
  } catch (err) {
    console.log(err);
    toastError(err);
  }
};

  const deleteMedia = async () => {
    if (attachment) {
      setAttachment(null);
      attachmentFile.current.value = null;
    }

    if (campaign.mediaPath) {
      await api.delete(`/campaigns/${campaign.id}/media-upload`);
      setCampaign((prev) => ({ ...prev, mediaPath: null, mediaName: null }));
      toast.success(i18n.t("campaigns.toasts.deleted"));
    }
  };

  const renderMessageField = (identifier) => {
    return (
      <div className={classes.field} key={identifier}>
        <label className={classes.label} htmlFor={identifier}>
          {i18n.t(`campaigns.dialog.form.${identifier}`)}
        </label>
        <Field
          as={TextField}
          id={identifier}
          name={identifier}
          fullWidth
          rows={5}
          placeholder={i18n.t("campaigns.dialog.form.messagePlaceholder")}
          multiline={true}
          variant="outlined"
          helperText="Utilize variáveis como {nome}, {numero}, {email} ou defina variáveis personalizadas."
          disabled={!campaignEditable && campaign.status !== "CANCELADA"}
        />
      </div>
    );
  };

  const renderConfirmationMessageField = (identifier) => {
    return (
      <div className={classes.field} key={identifier}>
        <label className={classes.label} htmlFor={identifier}>
          {i18n.t(`campaigns.dialog.form.${identifier}`)}
        </label>
        <Field
          as={TextField}
          id={identifier}
          name={identifier}
          fullWidth
          rows={5}
          placeholder={i18n.t("campaigns.dialog.form.messagePlaceholder")}
          multiline={true}
          variant="outlined"
          disabled={!campaignEditable && campaign.status !== "CANCELADA"}
        />
      </div>
    );
  };

  const cancelCampaign = async () => {
    try {
      await api.post(`/campaigns/${campaign.id}/cancel`);
      toast.success(i18n.t("campaigns.toasts.cancel"));
      setCampaign((prev) => ({ ...prev, status: "CANCELADA" }));
      resetPagination();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const restartCampaign = async () => {
    try {
      await api.post(`/campaigns/${campaign.id}/restart`);
      toast.success(i18n.t("campaigns.toasts.restart"));
      setCampaign((prev) => ({ ...prev, status: "EM_ANDAMENTO" }));
      resetPagination();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const filterOptions = createFilterOptions({
    trim: true,
  });

  return (
    <div className={classes.root}>
      <ConfirmationModal
        title={i18n.t("campaigns.confirmationModal.deleteTitle")}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      >
        {i18n.t("campaigns.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        scroll="paper"
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">
          {campaignEditable ? (
            <>
              {campaignId
                ? `${i18n.t("campaigns.dialog.update")}`
                : `${i18n.t("campaigns.dialog.new")}`}
            </>
          ) : (
            <>{`${i18n.t("campaigns.dialog.readonly")}`}</>
          )}
        </DialogTitle>
        <div style={{ display: "none" }}>
          <input
            type="file"
            ref={attachmentFile}
            onChange={(e) => handleAttachmentFile(e)}
          />
        </div>
        <Formik
          initialValues={campaign}
          enableReinitialize={true}
          validationSchema={CampaignSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveCampaign(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue }) => (
            <Form className={classes.form}>
              <DialogContent>
                <div className={classes.formGrid}>
                  <div className={`${classes.sectionTitle} ${classes.sectionTitleFirst}`}>
                    Dados da campanha
                  </div>

                  <div className={classes.field}>
                    <label className={classes.label} htmlFor="campaign-name">
                      {i18n.t("campaigns.dialog.form.name")}
                    </label>
                    <Field
                      as={TextField}
                      id="campaign-name"
                      name="name"
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      variant="outlined"
                      size="small"
                      fullWidth
                      disabled={!campaignEditable}
                    />
                  </div>

                  <div className={classes.field}>
                    <label className={classes.label} id="whatsapp-selection-label">
                      {i18n.t("campaigns.dialog.form.whatsapp")}
                    </label>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <Field
                        as={Select}
                        labelId="whatsapp-selection-label"
                        id="whatsappIds"
                        name="whatsappIds"
                        required
                        error={touched.whatsappId && Boolean(errors.whatsappId)}
                        disabled={!campaignEditable}
                        value={whatsappId}
                        onChange={(event) => {
                          setWhatsappId(event.target.value)
                        }}
                        displayEmpty
                        renderValue={(value) => {
                          const selected = (whatsapps || []).find((w) => String(w.id) === String(value));
                          if (selected) return selected.name;
                          // valor gravado que não está na lista: fica em branco, como o Select faria
                          return isNil(value) || value === "" ? <span className={classes.placeholder}>Selecione…</span> : "";
                        }}
                      >
                        {whatsapps &&
                          whatsapps.map((whatsapp) => (
                            <MenuItem key={whatsapp.id} value={whatsapp.id}>
                              {whatsapp.name}
                            </MenuItem>
                          ))}
                      </Field>
                    </FormControl>
                  </div>

                  <div className={classes.field}>
                    <label className={classes.label} id="contactList-selection-label">
                      {i18n.t("campaigns.dialog.form.contactList")}
                    </label>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <Field
                        as={Select}
                        labelId="contactList-selection-label"
                        id="contactListId"
                        name="contactListId"
                        error={touched.contactListId && Boolean(errors.contactListId)}
                        disabled={!campaignEditable}
                        displayEmpty
                      >
                        <MenuItem value="">Nenhuma</MenuItem>
                        {contactLists &&
                          contactLists.map((contactList) => (
                            <MenuItem key={contactList.id} value={contactList.id}>
                              {contactList.name}
                            </MenuItem>
                          ))}
                      </Field>
                    </FormControl>
                  </div>

                  <div className={classes.field}>
                    <label className={classes.label} id="tagList-selection-label">
                      {i18n.t("campaigns.dialog.form.tagList")}
                    </label>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <Field
                        as={Select}
                        labelId="tagList-selection-label"
                        id="tagListId"
                        name="tagListId"
                        error={touched.tagListId && Boolean(errors.tagListId)}
                        disabled={!campaignEditable}
                        displayEmpty
                        renderValue={(value) => {
                          const selected = Array.isArray(tagLists) && tagLists.find((tag) => String(tag.id) === String(value));
                          if (selected) return selected.name;
                          return isNil(value) || value === "" || value === "Nenhuma" ? <span className={classes.placeholder}>Nenhuma</span> : "";
                        }}
                      >
                        {Array.isArray(tagLists) &&
                          tagLists.map((tagList) => (
                            <MenuItem key={tagList.id} value={tagList.id}>
                              {tagList.name}
                            </MenuItem>
                          ))}
                      </Field>
                    </FormControl>
                  </div>

                  <div className={classes.field}>
                    <label className={classes.label} htmlFor="campaign-scheduledAt">
                      {i18n.t("campaigns.dialog.form.scheduledAt")}
                    </label>
                    <Field
                      as={TextField}
                      id="campaign-scheduledAt"
                      name="scheduledAt"
                      error={touched.scheduledAt && Boolean(errors.scheduledAt)}
                      helperText={touched.scheduledAt && errors.scheduledAt}
                      variant="outlined"
                      size="small"
                      type="datetime-local"
                      fullWidth
                      disabled={!campaignEditable}
                    />
                  </div>

                  <div className={classes.field}>
                    <label className={classes.label} id="confirmation-selection-label">
                      {i18n.t("campaigns.dialog.form.confirmation")}
                    </label>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <Field
                        as={Select}
                        labelId="confirmation-selection-label"
                        id="confirmation"
                        name="confirmation"
                        error={touched.confirmation && Boolean(errors.confirmation)}
                        disabled={!campaignEditable}
                      >
                        <MenuItem value={false}>Desabilitada</MenuItem>
                        <MenuItem value={true}>Habilitada</MenuItem>
                      </Field>
                    </FormControl>
                  </div>

                  {/* SEÇÃO DE RECORRÊNCIA */}
                  <div className={classes.sectionTitle}>Configuração de Recorrência</div>

                  <div className={classes.full}>
                    <FormControlLabel
                      control={
                        <Field
                          as={Switch}
                          color="primary"
                          name="isRecurring"
                          checked={values.isRecurring}
                          onChange={(e) => {
                            setFieldValue('isRecurring', e.target.checked);
                            if (!e.target.checked) {
                              setPreviewExecutions([]);
                              setShowPreview(false);
                            }
                          }}
                          disabled={!campaignEditable}
                        />
                      }
                      label="Habilitar recorrência"
                    />

                    <Collapse in={values.isRecurring}>
                      <div className={`${classes.formGrid} ${classes.recurrenceFields}`}>
                        <div className={classes.field}>
                          <label className={classes.label} id="recurrenceType-selection-label">
                            Tipo de Recorrência
                          </label>
                          <FormControl
                            variant="outlined"
                            size="small"
                            fullWidth
                            error={touched.recurrenceType && Boolean(errors.recurrenceType)}
                          >
                            <Field
                              as={Select}
                              labelId="recurrenceType-selection-label"
                              name="recurrenceType"
                              disabled={!campaignEditable}
                              onChange={(e) => {
                                setFieldValue('recurrenceType', e.target.value);
                                // Reset outros campos quando mudar tipo
                                setFieldValue('recurrenceDaysOfWeek', []);
                                setFieldValue('recurrenceDayOfMonth', 1);
                              }}
                              displayEmpty
                              renderValue={(value) => {
                                const selected = RECURRENCE_TYPES.find(([key]) => key === value);
                                return selected ? selected[1] : <span className={classes.placeholder}>Selecione…</span>;
                              }}
                            >
                              {RECURRENCE_TYPES.map(([key, label]) => (
                                <MenuItem key={key} value={key}>{label}</MenuItem>
                              ))}
                            </Field>
                            {touched.recurrenceType && errors.recurrenceType && (
                              <FormHelperText error>{errors.recurrenceType}</FormHelperText>
                            )}
                          </FormControl>
                        </div>

                        <div className={classes.field}>
                          <label className={classes.label} htmlFor="campaign-recurrenceInterval">
                            Intervalo
                          </label>
                          <Field
                            as={TextField}
                            id="campaign-recurrenceInterval"
                            name="recurrenceInterval"
                            type="number"
                            variant="outlined"
                            size="small"
                            fullWidth
                            inputProps={{ min: 1 }}
                            error={touched.recurrenceInterval && Boolean(errors.recurrenceInterval)}
                            helperText={
                              (touched.recurrenceInterval && errors.recurrenceInterval) ||
                              `A cada ${values.recurrenceInterval || 1} ${
                                values.recurrenceType === 'minutely' ? 'minuto(s)' :
                                values.recurrenceType === 'hourly' ? 'hora(s)' :
                                values.recurrenceType === 'daily' ? 'dia(s)' :
                                values.recurrenceType === 'weekly' ? 'semana(s)' :
                                values.recurrenceType === 'biweekly' ? 'quinzena(s)' :
                                values.recurrenceType === 'monthly' ? 'mês(es)' :
                                values.recurrenceType === 'yearly' ? 'ano(s)' : ''
                              }`
                            }
                            disabled={!campaignEditable}
                          />
                        </div>

                        {/* Dias da semana para recorrência semanal */}
                        {values.recurrenceType === 'weekly' && (
                          <div className={`${classes.field} ${classes.full}`}>
                            <span className={classes.label}>Dias da Semana</span>
                            <FormGroup row>
                              {daysOfWeekOptions.map((day) => (
                                <FormControlLabel
                                  key={day.value}
                                  control={
                                    <Checkbox
                                      color="primary"
                                      checked={values.recurrenceDaysOfWeek.includes(day.value)}
                                      onChange={(e) => {
                                        const currentDays = values.recurrenceDaysOfWeek || [];
                                        if (e.target.checked) {
                                          setFieldValue('recurrenceDaysOfWeek', [...currentDays, day.value]);
                                        } else {
                                          setFieldValue('recurrenceDaysOfWeek', 
                                            currentDays.filter(d => d !== day.value)
                                          );
                                        }
                                      }}
                                      disabled={!campaignEditable}
                                    />
                                  }
                                  label={day.label.substring(0, 3)}
                                />
                              ))}
                            </FormGroup>
                            {touched.recurrenceDaysOfWeek && errors.recurrenceDaysOfWeek && (
                              <FormHelperText error>{errors.recurrenceDaysOfWeek}</FormHelperText>
                            )}
                          </div>
                        )}

                        {/* Dia do mês para recorrência mensal */}
                        {values.recurrenceType === 'monthly' && (
                          <div className={`${classes.field} ${classes.full}`}>
                            <label className={classes.label} htmlFor="campaign-recurrenceDayOfMonth">
                              Dia do Mês
                            </label>
                            <Field
                              as={TextField}
                              id="campaign-recurrenceDayOfMonth"
                              name="recurrenceDayOfMonth"
                              type="number"
                              variant="outlined"
                              size="small"
                              fullWidth
                              inputProps={{ min: 1, max: 31 }}
                              error={touched.recurrenceDayOfMonth && Boolean(errors.recurrenceDayOfMonth)}
                              helperText={
                                (touched.recurrenceDayOfMonth && errors.recurrenceDayOfMonth) ||
                                "Dia específico do mês (1-31)"
                              }
                              disabled={!campaignEditable}
                            />
                          </div>
                        )}

                        <div className={classes.field}>
                          <label className={classes.label} htmlFor="campaign-recurrenceEndDate">
                            Data Final (opcional)
                          </label>
                          <Field
                            as={TextField}
                            id="campaign-recurrenceEndDate"
                            name="recurrenceEndDate"
                            type="date"
                            variant="outlined"
                            size="small"
                            fullWidth
                            error={touched.recurrenceEndDate && Boolean(errors.recurrenceEndDate)}
                            helperText={
                              (touched.recurrenceEndDate && errors.recurrenceEndDate) ||
                              "Deixe vazio para recorrência infinita"
                            }
                            disabled={!campaignEditable}
                          />
                        </div>

                        <div className={classes.field}>
                          <label className={classes.label} htmlFor="campaign-maxExecutions">
                            Máximo de Execuções (opcional)
                          </label>
                          <Field
                            as={TextField}
                            id="campaign-maxExecutions"
                            name="maxExecutions"
                            type="number"
                            variant="outlined"
                            size="small"
                            fullWidth
                            inputProps={{ min: 1 }}
                            error={touched.maxExecutions && Boolean(errors.maxExecutions)}
                            helperText={
                              (touched.maxExecutions && errors.maxExecutions) ||
                              "Deixe vazio para recorrência infinita"
                            }
                            disabled={!campaignEditable}
                          />
                        </div>

                        <div className={classes.full}>
                          <Button
                            variant="outlined"
                            startIcon={<VisibilityIcon />}
                            onClick={() => {
                              handlePreviewRecurrence(values);
                              setShowPreview(!showPreview);
                            }}
                            disabled={!values.recurrenceType || !values.scheduledAt}
                          >
                            {showPreview ? 'Ocultar' : 'Visualizar'} Próximas Execuções
                          </Button>

                          {/* Preview das execuções */}
                          <Collapse in={showPreview && previewExecutions.length > 0}>
                            <div className={classes.previewBox}>
                              <div className={classes.previewTitle}>Próximas 5 Execuções:</div>
                              <List dense disablePadding>
                                {previewExecutions.slice(0, 5).map((execution, index) => (
                                  <ListItem key={index} divider={index < Math.min(previewExecutions.length, 5) - 1} disableGutters>
                                    <ListItemText
                                      primary={`${index + 1}ª Execução`}
                                      secondary={typeof execution === 'string' ? execution : moment(execution).format('DD/MM/YYYY HH:mm')}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </div>
                          </Collapse>
                        </div>
                      </div>
                    </Collapse>
                  </div>

                  <div className={classes.sectionTitle}>Atendimento</div>

                  <div className={classes.field}>
                    <label className={classes.label} id="openTicket-selection-label">
                      {i18n.t("campaigns.dialog.form.openTicket")}
                    </label>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <Field
                        as={Select}
                        labelId="openTicket-selection-label"
                        id="openTicket"
                        name="openTicket"
                        error={touched.openTicket && Boolean(errors.openTicket)}
                        disabled={!campaignEditable}
                      >
                        <MenuItem value={"enabled"}>{i18n.t("campaigns.dialog.form.enabledOpenTicket")}</MenuItem>
                        <MenuItem value={"disabled"}>{i18n.t("campaigns.dialog.form.disabledOpenTicket")}</MenuItem>
                      </Field>
                    </FormControl>
                  </div>

                  <div className={classes.field}>
                    <label className={classes.label} id="statusTicket-selection-label">
                      {i18n.t("campaigns.dialog.form.statusTicket")}
                    </label>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <Field
                        as={Select}
                        labelId="statusTicket-selection-label"
                        id="statusTicket"
                        name="statusTicket"
                        error={touched.statusTicket && Boolean(errors.statusTicket)}
                        disabled={!campaignEditable || values.openTicket === 'disabled'}
                      >
                        <MenuItem value={"closed"}>{i18n.t("campaigns.dialog.form.closedTicketStatus")}</MenuItem>
                        <MenuItem value={"pending"}>{i18n.t("campaigns.dialog.form.pendingTicketStatus")}</MenuItem>
                        <MenuItem value={"open"}>{i18n.t("campaigns.dialog.form.openTicketStatus")}</MenuItem>
                      </Field>
                    </FormControl>
                  </div>

                  {/* SELECIONAR USUARIO */}
                  <div className={classes.field}>
                    <label className={classes.label} htmlFor="campaign-user">
                      Usuário
                    </label>
                    <Autocomplete
                      id="campaign-user"
                      getOptionLabel={(option) => `${option.name}`}
                      value={selectedUser}
                      size="small"
                      onChange={(e, newValue) => {
                        setSelectedUser(newValue);
                        if (newValue != null && Array.isArray(newValue.queues)) {
                          if (newValue.queues.length === 1) {
                            setSelectedQueue(newValue.queues[0].id);
                          }
                          setQueues(newValue.queues);
                        } else {
                          setQueues(allQueues);
                          setSelectedQueue("");
                        }
                      }}
                      options={options}
                      filterOptions={filterOptions}
                      freeSolo
                      fullWidth
                      autoHighlight
                      disabled={!campaignEditable || values.openTicket === 'disabled'}
                      noOptionsText={i18n.t("transferTicketModal.noOptions")}
                      loading={loading}
                      renderOption={option => (<span> <UserStatusIcon user={option} /> {option.name}</span>)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={i18n.t("transferTicketModal.fieldLabel")}
                          variant="outlined"
                          onChange={(e) => setSearchParam(e.target.value)}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <React.Fragment>
                                {loading ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </React.Fragment>
                            ),
                          }}
                        />
                      )}
                    />
                  </div>

                  <div className={classes.field}>
                    <label className={classes.label} id="queue-selection-label">
                      {i18n.t("transferTicketModal.fieldQueueLabel")}
                    </label>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <Select
                        labelId="queue-selection-label"
                        value={selectedQueue}
                        onChange={(e) => setSelectedQueue(e.target.value)}
                        required={!isNil(selectedUser)}
                        disabled={!campaignEditable || values.openTicket === 'disabled'}
                        displayEmpty
                        renderValue={(value) => {
                          const selected = queues.find((queue) => String(queue.id) === String(value));
                          if (selected) return selected.name;
                          return isNil(value) || value === "" ? (
                            <span className={classes.placeholder}>
                              {i18n.t("transferTicketModal.fieldQueuePlaceholder")}
                            </span>
                          ) : "";
                        }}
                      >
                        {queues.map((queue) => (
                          <MenuItem key={queue.id} value={queue.id}>
                            {queue.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>

                  {/* SEÇÃO DE MENSAGENS */}
                  <div className={classes.sectionTitle}>Mensagens</div>

                  <div className={classes.full}>
                    <Tabs
                      value={messageTab}
                      indicatorColor="primary"
                      textColor="primary"
                      onChange={(e, v) => setMessageTab(v)}
                      variant="scrollable"
                      scrollButtons="auto"
                      className={classes.tabs}
                    >
                      <Tab label="Msg. 1" index={0} />
                      <Tab label="Msg. 2" index={1} />
                      <Tab label="Msg. 3" index={2} />
                      <Tab label="Msg. 4" index={3} />
                      <Tab label="Msg. 5" index={4} />
                    </Tabs>
                    {values.confirmation ? (
                      <div className={classes.messageGrid}>
                        {renderMessageField(`message${messageTab + 1}`)}
                        {renderConfirmationMessageField(`confirmationMessage${messageTab + 1}`)}
                      </div>
                    ) : (
                      renderMessageField(`message${messageTab + 1}`)
                    )}
                  </div>

                  {(campaign.mediaPath || attachment) && (
                    <div className={classes.attachment}>
                      <AttachFileIcon />
                      <span className={classes.attachmentName}>
                        {attachment != null ? attachment.name : campaign.mediaName}
                      </span>
                      {campaignEditable && (
                        <Tooltip title={i18n.t("campaigns.confirmationModal.deleteTitle")}>
                          <IconButton
                            size="small"
                            aria-label={i18n.t("campaigns.confirmationModal.deleteTitle")}
                            className={classes.danger}
                            onClick={() => setConfirmationOpen(true)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                  )}
                </div>
              </DialogContent>
              <DialogActions className={classes.actions}>
                <div className={classes.actionsLeft}>
                  {campaign.status === "CANCELADA" && (
                    <Button
                      onClick={() => restartCampaign()}
                      variant="outlined"
                    >
                      {i18n.t("campaigns.dialog.buttons.restart")}
                    </Button>
                  )}
                  {campaign.status === "EM_ANDAMENTO" && (
                    <Button
                      onClick={() => cancelCampaign()}
                      className={classes.danger}
                    >
                      {i18n.t("campaigns.dialog.buttons.cancel")}
                    </Button>
                  )}
                  {!attachment && !campaign.mediaPath && campaignEditable && (
                    <Button
                      onClick={() => attachmentFile.current.click()}
                      disabled={isSubmitting}
                      variant="outlined"
                      startIcon={<AttachFileIcon />}
                    >
                      {i18n.t("campaigns.dialog.buttons.attach")}
                    </Button>
                  )}
                </div>
                <Button
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  {i18n.t("campaigns.dialog.buttons.close")}
                </Button>
                {(campaignEditable || campaign.status === "CANCELADA") && (
                  <Button
                    type="submit"
                    color="primary"
                    disabled={isSubmitting}
                    variant="contained"
                    className={classes.btnWrapper}
                  >
                    {campaignId
                      ? `${i18n.t("campaigns.dialog.buttons.edit")}`
                      : `${i18n.t("campaigns.dialog.buttons.add")}`}
                    {isSubmitting && (
                      <CircularProgress
                        size={24}
                        className={classes.buttonProgress}
                      />
                    )}
                  </Button>
                )}
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default CampaignModal;
